// ─────────────────────────────────────────────
//  TV-mode side panels (Prompt 2 + 3):
//    • TvCallPanel       — MD Call roster: names + phone numbers, call order.
//    • TvCrnaPanel       — CRNA roster grouped by shift (3/5/7/9 PM): names+time.
//    • TvBroadcastPanel  — AIC-only real-time announcements (write/edit/delete).
//
//  Presentational + self-contained. The broadcast panel writes through the
//  firestore broadcast helpers; only the day's AIC (canWrite) sees the composer
//  and edit/delete affordances — the security rules are the real boundary.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import { addBroadcast, updateBroadcast, deleteBroadcast } from "../firebase/firestore";
import {
  sortMdCall, mdCallBadge, mdCallLocationRole, mdCallBadgeColor,
} from "../features/sheet-import/attendingCallList";
import { facilityLabel, shortFacilityLabel, facilityShortTag } from "../domain/facilities";
import tokens from "../theme/tokens";

const { color, radius, font, space } = tokens;

// Shorten a campus-leader tag for the TV's tight badge — e.g. "Wesley Chapel
// AIC" → "WC AIC" — using THIS tenant's own facility docs, not a hardcoded
// pair of AdventHealth campus names.
//   1. Take the facility id straight off the role. It used to be recovered by
//      matching role.label back against the fixed label strings, which only
//      worked while those strings were constants: now that labels are derived
//      from each tenant's own facility names, a renamed campus — or any
//      tenant outside the three built-ins — would match nothing and silently
//      lose its abbreviation. An id is carried, never reverse-engineered from
//      a display string.
//   2. Look up that facility's short tag (facilityShortTag) — an editorial
//      abbreviation the tenant sets on its own facility doc, or the built-in
//      seed's ("WC"/"CAR"). No tag (e.g. today's "Tampa") → keep the full
//      label, unchanged from before this was pulled out of a hardcoded
//      .replace() chain.
//   3. Derive the bare campus name (no shared org prefix) from the tenant's
//      OWN live facility labels via shortFacilityLabel — the same derivation
//      NavMenu uses for the topbar tag — and swap in the short tag.
export function tvRoleTag(role, facilities = []) {
  if (!role) return "";
  const fid = role.facilityId;
  const tag = fid && facilityShortTag(fid, facilities);
  if (!fid || !tag) return role.label;
  const liveLabels = facilities.map((f) => f && (f.name || f.label)).filter(Boolean);
  // Pass undefined (not []) when there are no live labels yet — shortFacilityLabel
  // falls back to the built-in seed's own labels to find the org prefix, rather
  // than treating an empty array as "no shared prefix exists" and leaving the
  // org name (e.g. "AdventHealth") stuck on the front of the campus name.
  const campusName = shortFacilityLabel(
    facilityLabel(fid, facilities),
    liveLabels.length ? liveLabels : undefined
  );
  return role.label.replace(campusName, tag);
}

// ── Shared panel shell ──
// bodyMaxHeight turns the panel body into its own scroll window so a long list
// (e.g. a full-hospital MD call sheet) stays inside a fixed box instead of
// pushing the other panels off the TV.
function Panel({ title, count, children, bodyMaxHeight }) {
  return (
    <section style={s.panel} aria-label={title}>
      <div style={s.panelHead}>
        <span style={s.panelTitle}>{title}</span>
        {count != null && <span style={s.panelCount}>{count}</span>}
      </div>
      <div style={{ ...s.panelBody, ...(bodyMaxHeight ? { maxHeight: bodyMaxHeight } : {}) }}>
        {children}
      </div>
    </section>
  );
}

// ── Panel 1: MD Call roster ──
// callList (preferred) = the sheet-imported facility-wide attending call list
// ({ code, name, callOrder, postCall, wc, isAIC }) rendered EXACTLY like the
// Daily Assignments MdCallPanel — PC→num→L badge, WC red / other navy, AIC tag.
// items = fallback (rostered AIC + supervisors) when no sheet was imported.
// bodyMaxHeight caps it to its own scroll window when the list is long.
// onRemove(code) — when provided (TV admin), each sheet-mode row gets a clean X
// to drop that doctor from the day's MD call list when they leave.
export function TvCallPanel({ items = [], callList = null, bodyMaxHeight, onRemove = null, facilityBoards = {}, facilities = [] }) {
  const useSheet = Array.isArray(callList) && callList.length > 0;
  const listed = useSheet ? sortMdCall(callList) : items;
  // Location colour-coding: badge tinted by the doctor's campus (red WC /
  // blue Tampa / green CAR); NAMES STAY BLACK; leaders keep a labelled tag.
  const roleOf = (e) => mdCallLocationRole(e, facilityBoards.aicByFacility || {}, facilities);
  const badgeColorOf = (e) => mdCallBadgeColor(e, { ...facilityBoards, facilities });
  return (
    <Panel title="MD CALL" count={listed.length} bodyMaxHeight={bodyMaxHeight}>
      {listed.length === 0 ? (
        <div style={s.empty}>No doctors on call.</div>
      ) : useSheet ? (
        listed.map((e, i) => (
          <div key={`${e.code || "md"}-${i}`} style={s.callRow}>
            <span
              style={{ ...s.callBadge, color: "#fff", background: badgeColorOf(e) || (e.wc ? color.accent.error : color.accent.primary), borderColor: badgeColorOf(e) || (e.wc ? color.accent.error : color.accent.primary) }}
              title={roleOf(e)?.label}
            >
              {mdCallBadge(e)}
            </span>
            <span style={s.callName}>{e.name || e.code}</span>
            {roleOf(e) && (
              <span style={{ ...s.callBadgeAic, color: roleOf(e).color, borderColor: roleOf(e).color }}>
                {tvRoleTag(roleOf(e), facilities)}
              </span>
            )}
            {!roleOf(e) && e.wc && e.isAIC && <span style={s.callBadgeAic}>AIC</span>}
            {e.isLocum && <span style={s.callBadgeLocum}>LOCUM</span>}
            {onRemove && (
              <button
                style={s.callX}
                onClick={() => onRemove(e.code)}
                aria-label={`Remove ${e.name || e.code} from MD call`}
                title="Remove from call list"
              >
                ✕
              </button>
            )}
          </div>
        ))
      ) : (
        listed.map((m) => (
          <div key={m.id} style={s.callRow}>
            {/* Fallback (no sheet): call number from the daily roster — shown
                for the AIC too when they hold one (e.g. #14). A non-AIC doctor
                with none shows "L" (last); an AIC with none shows a hidden
                spacer to keep names aligned. AIC marker pill sits at the right. */}
            <span style={{ ...s.callBadge, ...(badgeColorOf(m) ? { background: badgeColorOf(m), borderColor: badgeColorOf(m), color: "#fff" } : {}), ...(m.isAic && m.callOrder == null ? { visibility: "hidden" } : {}) }}>
              {m.callOrder != null ? m.callOrder : (m.isAic ? "·" : "L")}
            </span>
            <span style={s.callName} title={roleOf(m)?.label}>{m.name}</span>
            {m.isAic && <span style={s.callBadgeAic}>AIC</span>}
          </div>
        ))
      )}
    </Panel>
  );
}

// ── Panel 2: CRNA roster by shift group ──
// On-call CRNAs (Grace Elaro etc.) get their own "On call · 24 hrs" group with
// an amber header and a "7a–7a" time, matching the Daily roster's CrnaRosterPanel.
export function TvCrnaPanel({ groups = [], bodyMaxHeight }) {
  const total = groups.reduce((n, g) => n + g.members.length, 0);
  return (
    <Panel title="CRNA / AA" count={total} bodyMaxHeight={bodyMaxHeight}>
      {groups.length === 0 ? (
        <div style={s.empty}>No CRNAs scheduled.</div>
      ) : (
        groups.map((g) => (
          <div key={g.label} style={s.shiftGroup}>
            <div style={{ ...s.shiftLabel, ...(g.onCall ? s.shiftLabelOnCall : {}) }}>{g.label}</div>
            {g.members.map((mem, i) => (
              <div key={`${mem.id || "crna"}-${i}`} style={{ ...s.crnaRow, ...(g.onCall ? s.crnaRowOnCall : {}) }}>
                <span style={s.crnaName}>{mem.name}</span>
                {mem.timeLabel && <span style={{ ...s.crnaTime, ...(g.onCall ? s.crnaTimeOnCall : {}) }}>{mem.timeLabel}</span>}
              </div>
            ))}
          </div>
        ))
      )}
    </Panel>
  );
}

// ── Panel: Unassigned / available staff ──
// Rostered CRNAs/AAs + doctors not currently in a room — the people free to
// pick up a room or COVER A BREAK (Prompt 9). Shown on the wall so the team can
// see who's available at a glance. Doctors first, then CRNAs/AAs.
export function TvUnassignedPanel({ doctors = [], crnas = [], bodyMaxHeight }) {
  const total = doctors.length + crnas.length;
  return (
    <Panel title="UNASSIGNED" count={total} bodyMaxHeight={bodyMaxHeight}>
      {total === 0 ? (
        <div style={s.empty}>Everyone is assigned.</div>
      ) : (
        <>
          {doctors.map((d, i) => (
            <div key={`d-${d.id || i}`} style={s.benchRow}>
              <span style={s.benchName}>{d.name}</span>
              <span style={s.benchRole}>Doctor</span>
            </div>
          ))}
          {crnas.map((c, i) => (
            <div key={`c-${c.id || i}`} style={s.benchRow}>
              <span style={s.benchName}>{c.name}</span>
              <span style={s.benchRole}>{(c.kind || c.subRole || "CRNA").toUpperCase()}</span>
            </div>
          ))}
        </>
      )}
    </Panel>
  );
}

// ── Panel 3: Broadcast messages (AIC writes) ──
export function TvBroadcastPanel({ broadcasts = [], canWrite = false, facilityId, dateKey, author, bodyMaxHeight }) {
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(null); // {id, text}
  const [busy, setBusy] = useState(false);

  const post = async () => {
    const text = draft.trim();
    if (!text || !canWrite) return;
    setBusy(true);
    try {
      await addBroadcast({ facilityId, dateKey, authorId: author?.id || null, authorName: author?.name || null, text });
      setDraft("");
    } catch (e) { console.error("broadcast post failed:", e); }
    finally { setBusy(false); }
  };
  const saveEdit = async () => {
    const text = (editing?.text || "").trim();
    if (!text) return;
    setBusy(true);
    try { await updateBroadcast(editing.id, text); setEditing(null); }
    catch (e) { console.error("broadcast edit failed:", e); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    setBusy(true);
    try { await deleteBroadcast(id); }
    catch (e) { console.error("broadcast delete failed:", e); }
    finally { setBusy(false); }
  };

  return (
    <Panel title="📢 BROADCAST" count={broadcasts.length} bodyMaxHeight={bodyMaxHeight}>
      {canWrite && (
        <div style={s.bcComposer}>
          <input
            style={s.bcInput}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") post(); }}
            placeholder="Post an announcement…"
            aria-label="New broadcast message"
            disabled={busy}
          />
          <button style={s.bcSend} onClick={post} disabled={busy || !draft.trim()}>Post</button>
        </div>
      )}
      {broadcasts.length === 0 ? (
        <div style={s.empty}>No announcements.</div>
      ) : (
        broadcasts.map((b) => (
          <div key={b.id} style={s.bcMsg}>
            {editing?.id === b.id ? (
              <div style={s.bcComposer}>
                <input
                  style={s.bcInput}
                  value={editing.text}
                  onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); }}
                  aria-label="Edit broadcast message"
                  disabled={busy}
                />
                <button style={s.bcSend} onClick={saveEdit} disabled={busy}>Save</button>
                <button style={s.bcGhost} onClick={() => setEditing(null)} disabled={busy}>✕</button>
              </div>
            ) : (
              <>
                <div style={s.bcText}>{b.text}</div>
                {canWrite && (
                  <div style={s.bcActions}>
                    <button style={s.bcGhost} onClick={() => setEditing({ id: b.id, text: b.text })} aria-label="Edit">✎</button>
                    <button style={s.bcGhost} onClick={() => remove(b.id)} aria-label="Delete">🗑</button>
                  </div>
                )}
              </>
            )}
          </div>
        ))
      )}
    </Panel>
  );
}

const s = {
  panel: {
    background: color.surface.card, border: `1px solid ${color.border.default}`,
    borderRadius: radius.md, display: "flex", flexDirection: "column", minHeight: 0,
    overflow: "hidden",
  },
  panelHead: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "8px 12px", borderBottom: `1px solid ${color.border.default}`,
    background: color.surface.raised,
  },
  panelTitle: { fontSize: 13, fontWeight: font.weight.bold, color: color.text.primary, letterSpacing: ".5px", textTransform: "uppercase" },
  panelCount: { fontSize: 11, fontWeight: font.weight.bold, color: color.text.muted, background: color.surface.card, border: `1px solid ${color.border.default}`, borderRadius: radius.full, padding: "0 8px" },
  panelBody: { padding: 8, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 },
  empty: { fontSize: 12, color: color.text.muted, padding: "6px 4px" },

  // MD Call
  callRow: { display: "flex", alignItems: "center", gap: 8, background: color.surface.base, borderRadius: radius.sm, padding: "6px 8px" },
  callBadge: { flexShrink: 0, minWidth: 22, textAlign: "center", fontSize: 11, fontWeight: font.weight.bold, color: color.accent.primary, background: "#e8edf9", border: `1px solid ${color.accent.primary}`, borderRadius: radius.sm, padding: "1px 4px" },
  // AIC marker pill, shown to the RIGHT of the name (no longer a left badge).
  callBadgeAic: { flexShrink: 0, fontSize: 10, fontWeight: font.weight.bold, color: "#7a4f00", background: "#f59e0b20", border: "1px solid #f59e0b", borderRadius: radius.full, padding: "1px 8px", letterSpacing: ".4px" },
  callName: { flex: 1, minWidth: 0, fontSize: 13, fontWeight: font.weight.bold, color: color.text.primary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  // Locum tag + clean remove X (TV admin removes a doctor who left).
  callBadgeLocum: { flexShrink: 0, fontSize: 10, fontWeight: font.weight.bold, color: "#3730a3", background: "#e0e7ff", border: "1px solid #a5b4fc", borderRadius: radius.full, padding: "1px 8px", letterSpacing: ".4px" },
  callX: { flexShrink: 0, background: "transparent", border: "none", color: color.text.muted, fontSize: 13, lineHeight: 1, cursor: "pointer", padding: "2px 4px", borderRadius: radius.sm },

  // CRNA roster
  shiftGroup: { display: "flex", flexDirection: "column", gap: 2 },
  shiftLabel: { fontSize: 11, fontWeight: font.weight.bold, color: color.text.muted, textTransform: "uppercase", letterSpacing: ".5px", marginTop: 2, padding: "2px 6px", borderRadius: radius.sm },
  // On-call group header — amber pill (matches CrnaRosterPanel.groupHeaderOnCall).
  shiftLabelOnCall: { background: "#ffe6ad", border: "1px solid #f4d488", color: "#9a6712" },
  crnaRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "3px 6px", background: color.surface.base, borderRadius: radius.sm },
  crnaRowOnCall: { background: "#fff7e6" },
  crnaName: { fontSize: 13, color: color.text.primary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  crnaTime: { fontSize: 11, fontWeight: font.weight.bold, color: color.text.muted, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
  crnaTimeOnCall: { color: "#9a6712" },

  // Unassigned bench
  benchRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "4px 8px", background: color.surface.base, borderRadius: radius.sm },
  benchName: { fontSize: 13, fontWeight: font.weight.semibold, color: color.text.primary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  benchRole: { flexShrink: 0, fontSize: 10, fontWeight: font.weight.bold, color: color.text.muted, textTransform: "uppercase", letterSpacing: ".3px" },

  // Broadcast
  bcComposer: { display: "flex", gap: 6, alignItems: "center" },
  bcInput: { flex: 1, minWidth: 0, background: color.surface.card, border: `1px solid ${color.border.default}`, borderRadius: radius.sm, padding: "6px 8px", fontSize: 13, fontFamily: "inherit", color: color.text.primary },
  bcSend: { flexShrink: 0, background: color.accent.primary, color: "#fff", border: "none", borderRadius: radius.sm, padding: "6px 10px", fontSize: 12, fontWeight: font.weight.bold, cursor: "pointer" },
  bcGhost: { flexShrink: 0, background: "transparent", border: `1px solid ${color.border.default}`, color: color.text.muted, borderRadius: radius.sm, padding: "4px 8px", fontSize: 12, cursor: "pointer" },
  bcMsg: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, background: "#1e40af0a", border: `1px solid #1e40af33`, borderRadius: radius.sm, padding: "7px 9px" },
  bcText: { flex: 1, minWidth: 0, fontSize: 13, color: color.text.primary, lineHeight: 1.3, wordBreak: "break-word" },
  bcActions: { display: "flex", gap: 4, flexShrink: 0 },
};
